import React, { useEffect, useState } from "react";
import CalenderDropdown from "@_common/CalenderDropdown";
import DashboardLayout from "@layouts/dashboard/dashboard-layout";
import moment from "moment";
import qs from "query-string";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getValue } from "@utils/lodash";
import { exportManageAudit, vendorReportsAccount } from "@services/reports.service";
import Loader from "@components/common/Loader";
import ScrollableReactTable from "@components/ScrollableDatatable";
import Notfound from "@components/Not-Found";
import PaginationPage from "@components/pagination/pagination";
import { QueryRequestHelper } from "@_common/query-request-helper";
import { tableScoreColors } from "@_common/colors";
import { numberFormatter } from "@_common/text-helper";
import SearchHeader from "@components/common/SearchHeader";
import { handleDownloadOnClick } from "@_common/downloadfile";

interface ReportsAccountProps {}
const ReportsAccount: React.FunctionComponent<ReportsAccountProps> = () => {
  let navigate = useNavigate();

  /* -------------------------------------------------------------------------- */
  /*                               UseState Section                             */
  /* -------------------------------------------------------------------------- */
  const [date, setDate] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [list, setList] = useState([]);
  const [selectedIDs, setSelectedIDs] = useState<any>([]);
  const [check, setCheck] = useState(false);

  /* -------------------------------------------------------------------------- */
  /*                               UseEffect Section                            */
  /* -------------------------------------------------------------------------- */

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if (Object.keys(params).length === 0) {
      getData();
      setDate(new Date());
    }
  }, []);

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if (Object.keys(params).length > 0) {
      getData();

      if (params.page_no) {
        setPage_no(parseInt(params.page_no));
      }

      if (params.limit) {
        setLimit(parseInt(params.limit));
      }

      if (params.date) {
        setDate(new Date(params.date));
      } else {
        setDate(new Date());
      }
    }
  }, [window.location.href]);

  /* -------------------------------------------------------------------------- */
  /*                               Date section                             */
  /* -------------------------------------------------------------------------- */

  const handleSubmitDate = () => {
    let dates = moment(date).format("YYYY-MM-DD");
    if (dates && dates !== "Invalid date") {
      let payload: any = {
        date: dates,
      };
      // removing null values
      Object.keys(payload).forEach((key) => {
        if (!payload[key] || payload[key] === undefined) {
          delete payload[key];
        }
      });
      let queryRequest = qs.stringify(payload);
      navigate(`${window.location.pathname}?${queryRequest}`);
    } else {
      toast.error("Please select date");
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                               API Section                                  */
  /* -------------------------------------------------------------------------- */

  const getData = async () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let dates = date ? date : new Date();
    let user = JSON.parse(getValue(localStorage, `user`, {}));
    let vendor_id = getValue(user, `vendor.id`, "");
    let user_id = getValue(user, `id`, "");
    let payload: any = {
      month: params.date
        ? params.date.split("-")[1]
        : date
        ? moment(date).format("MM")
        : moment(dates).format("MM"),
      year: params.date
        ? params.date.split("-")[0]
        : date
        ? moment(date).format("YYYY")
        : moment(dates).format("YYYY"),
      vendor_id: vendor_id,
      user_id: user_id,
      type: "GROUP",
      page_no: params.page_no ? parseInt(params.page_no) : 1,
      page_size: params.limit ? parseInt(params.limit) : 100,
      search_text: params.search_text,
      sort_by: params.sort_by,
      is_redirect: true,
    };
    // removing null values
    Object.keys(payload).forEach((key) => {
      if (!payload[key] || payload[key] === undefined) {
        delete payload[key];
      }
    });
    let queryRequest = qs.stringify(payload);
    setIsLoading(true);
    try {
      let resp = await vendorReportsAccount(queryRequest);
      if (resp) {
        setIsLoading(false);
        setList(getValue(resp, `data`, []));
        setTotalCount(getValue(resp, `data.pagination.total`, 0));
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
    }
  };

  /* -------------------------------------------------------------------------- */
  /*                               React Datatable                              */
  /* -------------------------------------------------------------------------- */
  const searchParams = new URLSearchParams(window.location.search);
  const paramsNew = Object.fromEntries(searchParams.entries());

  const dateParam = paramsNew?.date ? new Date(paramsNew.date) : new Date(); // Get the date from params or use the current date

  const getMonthNameWithYear = (date: Date, offset: number) => {
    const adjustedDate = new Date(date);
    adjustedDate.setMonth(date.getMonth() - offset); // Subtract months to get previous month
    const month = adjustedDate.toLocaleString("default", { month: "short" });
    const year = adjustedDate.getFullYear().toString().slice(-2);
    return `${month}-${year}`;
  };

  // Use the new function to get month-year combinations
  const currentMonthName = getMonthNameWithYear(dateParam, 0);
  const prevMonth1Name = getMonthNameWithYear(dateParam, 1);
  const prevMonth2Name = getMonthNameWithYear(dateParam, 2);
  const prevMonth3Name = getMonthNameWithYear(dateParam, 3);

  const columns = React.useMemo(
    () => [
      {
        Header: "Account Name",
        accessor: "group_name",
        width: 150,
        Cell: ({ row }: any) => {
          return <>{row.values.group_name}</>;
        },
      },

      {
        Header: `${currentMonthName} Score`,
        accessor: "score",
        width: 120,
        Cell: ({ row }: any) => {
          return (
            <div
              className="score-button"
              style={{
                backgroundColor: tableScoreColors(
                  getValue(row, `values.score`, 0)
                    ? parseFloat(getValue(row, `values.score`, 0))
                    : 0
                ),
              }}
            >
              {getValue(row, `values.score`, null) === null
                ? "- -"
                : `${getValue(row, `values.score`, 0)}%`}
            </div>
          );
        },
      },
      {
        Header: `${prevMonth1Name} Score`,
        accessor: "prev_month_1_score",
        width: 120,
        Cell: ({ row }: any) => {
          return (
            <div
              className="score-button"
              style={{
                backgroundColor: tableScoreColors(
                  getValue(row, `values.prev_month_1_score`, 0)
                    ? parseFloat(getValue(row, `values.prev_month_1_score`, 0))
                    : 0
                ),
              }}
            >
              {getValue(row, `values.prev_month_1_score`, null) === null
                ? "- -"
                : `${getValue(row, `values.prev_month_1_score`, 0)}%`}
            </div>
          );
        },
      },
      {
        Header: `${prevMonth2Name} Score`,
        accessor: "prev_month_2_score",
        width: 120,
        Cell: ({ row }: any) => {
          return (
            <div
              className="score-button"
              style={{
                backgroundColor: tableScoreColors(
                  getValue(row, `values.prev_month_2_score`, 0)
                    ? parseFloat(getValue(row, `values.prev_month_2_score`, 0))
                    : 0
                ),
              }}
            >
              {getValue(row, `values.prev_month_2_score`, null) === null
                ? "- -"
                : `${getValue(row, `values.prev_month_2_score`, 0)}%`}
            </div>
          );
        },
      },
      {
        Header: `${prevMonth3Name} Score`,
        accessor: "prev_month_3_score",
        width: 120,
        Cell: ({ row }: any) => {
          return (
            <div
              className="score-button"
              style={{
                backgroundColor: tableScoreColors(
                  getValue(row, `values.prev_month_3_score`, 0)
                    ? parseFloat(getValue(row, `values.prev_month_3_score`, 0))
                    : 0
                ),
              }}
            >
              {getValue(row, `values.prev_month_3_score`, null) === null
                ? "- -"
                : `${getValue(row, `values.prev_month_3_score`, 0)}%`}
            </div>
          );
        },
      },
      {
        Header: "Sites",
        accessor: "location",
        width: 80,
      },
      // {
      //   Header: "Vendors",
      //   accessor: "vendor",
      //   width: 80,
      // },
      // {
      //   Header: "Absent",
      //   accessor: "absent_sites",
      //   width: 100,
      // },
      {
        Header: "Submitted Sites",
        accessor: "locations_all_submitted",
        width: 80,
      },
      {
        Header: "Partially Submitted Sites",
        accessor: "locations_partial_submission",
        width: 120,
      },
      {
        Header: "Not Submitted Sites",
        accessor: "locations_no_submission",
        width: 100,
      },
      {
        Header: "Headcount",
        accessor: "manpower",
        width: 80,
        Cell: ({ row }: any) => {
          return (
            <div className="headcount-button">
              {getValue(row, `values.manpower`, 0)
                ? numberFormatter(getValue(row, `values.manpower`, 0))
                : 0}
            </div>
          );
        },
      },
    ],
    [paramsNew.date]
  );

  const getTableState = (value: object) => {
    let selectedIndexList = Object.keys(
      getValue(value, `selectedRowIds`, {})
    ).map((item) => parseInt(item));
    let selectedIds =
      list.length > 0
        ? list.filter((_item: object, index: number) =>
            selectedIndexList.includes(index)
          )
        : [];
    if (selectedIds.length > 0) {
      setSelectedIDs(
        selectedIds.map((item: object) => getValue(item, `id`, ""))
      );
    } else {
      setSelectedIDs([]);
    }
  };

  const getTrProps = (rowInfo: any) => {
    if (getValue(rowInfo, `id`, "")) {
      return {
        style: {},
      };
    }
    return {};
  };

  /* -------------------------------------------------------------------------- */
  /*                               Pagination section                           */
  /* -------------------------------------------------------------------------- */

  const [totalCount, setTotalCount] = useState(0);
  const [limit, setLimit] = useState(100);
  const [page_no, setPage_no] = useState(1);

  const handleChangePagination = (page_no: string) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let payload = {
      ...params,
      page_no: page_no,
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
  };

  const handleChangeLimit = (limit: string) => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let payload: any = {
      ...params,
      limit: limit,
      page_no: "1",
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
  };

  /* -------------------------------------------------------------------------- */
  /*                               Search section                               */
  /* -------------------------------------------------------------------------- */
  const [showSearchTextbox, setShowSearchTextbox] = useState(false);
  const [search_text, setSearchInput] = useState<any>("");

  const toggleSearch = () => {
    setShowSearchTextbox((prevState) => !prevState);
  };
  const toggleSearchClose = () => {
    setSearchInput("");
    toggleSearch();
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    if (params.search_text) {
      delete params.search_text;
      let payload: any = { ...params };
      let queryRequest = QueryRequestHelper(payload);
      navigate(`${window.location.pathname}?${queryRequest}`);
      getData();
    }
  };

  const handleChangeSearch = async (e: any) => {
    setSearchInput(e.target.value);
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());

    let payload: any = {
      date: params.date,
      search_text: e.target.value,
    };
    let queryRequest = QueryRequestHelper(payload);
    navigate(`${window.location.pathname}?${queryRequest}`);
    if (!e.target.value) {
      getData();
    }
  };


  /* -------------------------------------------------------------------------- */
  /*                             Download Reports                               */
  /* -------------------------------------------------------------------------- */
  const [downloadLoading, setDownloadLoading] = useState(false);
  const handleDownloadSelfComplianceReport = async () => {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    let dates = date ? date : new Date();
    let userSpec = getValue(localStorage, `user`, {});
    let user: any = JSON.parse(userSpec);
    let vendor_id = getValue(user, `vendor.id`, "");


    let payload: any = {
      month: params.date
        ? params.date.split("-")[1]
        : date
        ? moment(date).format("MM")
        : moment(dates).format("MM"),
      year: params.date
        ? params.date.split("-")[0]
        : date
        ? moment(date).format("YYYY")
        : moment(dates).format("YYYY"),
      page_no: params.page_no ? parseInt(params.page_no) : 1,
      page_size: params.limit ? parseInt(params.limit) : limit,
      search_text: params.search_text,
      sort_by: params.sort_by,
      region_id: params.region_id,
      group_id: params.group_id,
      location_id: params.location_id,
      vendor_id: params.vendor_id ? params.vendor_id : vendor_id, 
      // type: params.type ? params.type : "REGION",
      audit_type: params.audit_type,
      state_id: getValue(params, `state_id`, ""),
      city_id: getValue(params, `city_id`, ""),
      is_redirect: true,
      user_id:getValue(user,`id`,''),
      type: "GROUP",
    };

    Object.keys(payload).forEach((key) => {
      if (!payload[key] || payload[key] === undefined) {
        delete payload[key];
      }
    });

    let queryRequest = QueryRequestHelper(payload)
    setDownloadLoading(true);
    try {
      let resp = await exportManageAudit(queryRequest);
      if (resp) {
        setDownloadLoading(false);
        toast.success(getValue(resp, `message`, ""));
        // navigate(`/self-compliance-logs/SELFCOMP_MY_TASKS`);
        handleDownloadOnClick(
          getValue(resp, `data.downloadUrl`, ""),
          "Manage Audit"
        );
      } else {
        setDownloadLoading(false);
      }
    } catch (error) {
      setDownloadLoading(false);
    }
  };

  return (
    <>
      <DashboardLayout>
        <div className="dashboard-wrapper__header d-flex align-items-center justify-content-between">
          <h4 className="dashbaord-wrapper__header-title">
            Accounts
          </h4>

          <div className="d-flex align-items-center">
            <CalenderDropdown
              onChange={setDate}
              value={date}
              handleSubmitDate={handleSubmitDate}
            />

            <div className="header-searchbar-wrapper">
              <SearchHeader
                searchInput={search_text}
                showSearchTextbox={showSearchTextbox}
                toggleSearch={toggleSearch}
                toggleSearchClose={toggleSearchClose}
                handleChangeSearch={handleChangeSearch}

                downloadSelfComplianceReport={true}
                handleDownloadSelfComplianceReport={
                  handleDownloadSelfComplianceReport
                }
                downloadLoading={downloadLoading}
                addVisible={false}
              />
            </div>
          </div>
        </div>

        <div className="admin-wrapper position-relative">
          {isLoading ? (
            <Loader />
          ) : getValue(list, `length`, 0) > 0 ? (
            <ScrollableReactTable
              columns={columns}
              data={list}
              getTableState={getTableState}
              // rowClick={rowClick}
              check={check}
              selectedIDs={selectedIDs}
              checkboxlogo
              getTrProps={getTrProps}
            />
          ) : (
            <>
              {getValue(list, `length`, 0) !== 0 ? (
                <>
                  <Notfound />
                </>
              ) : (
                <Notfound />
              )}
            </>
          )}
        </div>

        {/* Marking Pagination as a d-none because in api im not getting pagination */}
        <div className="d-flex justify-content-between d-none">
          {getValue(list, `length`, 0) > 0 && (
            <img
              src="/images/profile-illustration.svg"
              className="img-fluid px-5 py-3"
            />
          )}
          {totalCount > 10 ? (
            <PaginationPage
              totalCount={totalCount}
              limit={limit}
              pageNumber={page_no}
              handleChangePagination={handleChangePagination}
              handleChangeLimit={handleChangeLimit}
            />
          ) : (
            <p className="show-rows-count mt-3 d-flex">
              Total Count:&nbsp;&nbsp;{" "}
              <p style={{ color: "rgb(13, 110, 253)" }}>{totalCount}</p>
            </p>
          )}
        </div>
      </DashboardLayout>
    </>
  );
};

export default ReportsAccount;
